import {view, css} from '@sail/ui/deprecated';
import {useEffect, useRef, useState} from 'react';
import {MessageDescriptor} from 'react-intl';
import {CloseButton} from './CloseButton';
import {HeaderTitle} from './HeaderTitle';
import {HeaderMenu} from './HeaderMenu';
import {ToggleMinimizeButton} from './ToggleMinimizeButton';
import {useWidget, useWidgetControls} from '../useWidget';
import {useTheme} from '../../ThemeProvider';
import {SpeakerButton} from './SpeakerButton';

type Props = {
  defaultTitle?:
    | string
    | MessageDescriptor
    | React.ReactNode
    | undefined
    | null;
};

export function Header({defaultTitle}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const {setMinimizedHeight} = useWidgetControls();
  const theme = useTheme();

  const {header} = useWidget();
  const [showHeader, setShowHeader] = useState<boolean>(header.isVisible());
  const [headerDisabled, setHeaderDisabled] = useState<boolean>(
    header.isDisabled(),
  );

  useEffect(() => {
    const sub = header.visibilityObservable().subscribe(setShowHeader);
    return () => sub.unsubscribe();
  }, [header]);

  useEffect(() => {
    const sub = header.disabledObservable().subscribe(setHeaderDisabled);
    return () => sub.unsubscribe();
  }, [header]);

  useEffect(() => {
    if (ref.current) {
      setMinimizedHeight(ref.current.offsetHeight);
    }
  }, [setMinimizedHeight]);

  if (!showHeader) {
    return null;
  }

  return (
    <view.div
      ref={ref}
      data-testid="conversations-widget-header"
      css={{
        userSelect: css.value('none'),
        backgroundColor: css.value(theme.brandColor),
        paddingY: 'small',
        paddingX: 150, // 12px on the left and right
        stack: 'x',
        alignY: 'center',
        position: css.value('relative'),

        // Regrettably, for the header to be exactly 50px tall,
        // restricting the maxHeight of its content is the only way atm.
        maxHeight: css.value('50px'),

        // important isn't (or shouldn't be) needed here,
        // but I've noticed in storybook that the parent style
        // (stack: 'y', // which does '.parent-style > *')
        // sometimes gets flopped such that it is taking
        // precedence over this style. it's rare and is
        // likely specific to how storybook loads styles,
        // but this is a safeguard
        flexGrow: css.value('0 !important'),
      }}
    >
      {headerDisabled ? (
        <view.div
          data-testid="conversations-widget-header-disabled-dimmer"
          css={{
            position: css.value('absolute'),
            zIndex: css.value(10),
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            opacity: css.value('0.3'),
            backgroundColor: css.value('#000000'),
          }}
        />
      ) : null}

      <view.div
        css={{
          stack: 'x',
          alignY: 'center',
          alignX: 'start',
          width: css.value('100%'),
          height: css.value('100%'),
        }}
      >
        <HeaderMenu />
        <HeaderTitle defaultTitle={defaultTitle} />
      </view.div>

      <view.div
        css={{
          stack: 'x',
          alignY: 'center',
          alignX: 'end',
          gap: 'small',
          height: 'fill',
        }}
      >
        <SpeakerButton disabled={headerDisabled} />
        <ToggleMinimizeButton disabled={headerDisabled} />
        <CloseButton disabled={headerDisabled} />
      </view.div>
    </view.div>
  );
}
